import PageHero from "@/components/PageHero";
import { StaggerChildren, StaggerItem } from "@/components/Motion";
import { Award, ExternalLink } from "lucide-react";

import SeoHead from "@/components/SeoHead";
import { SectionLoading, SectionError, SectionEmpty } from "@/components/RichContent";
import { useAwards } from "@/hooks/usePublicData";

export default function Awards() {
  const { data, isLoading, isError, refetch } = useAwards();
  const items = (data && data.items) || [];

  return (
    <>
      <SeoHead pageKey="awards" />

      <PageHero
        eyebrow="Awards & Recognition"
        chapter="Chapter — Recognition"
        lines={["Recognition, at the", "company level."]}
        blurb="Company-level awards and industry recognition. Individual agent awards are not listed."
      />

      <section className="bg-[#060f1e] py-16 lg:py-24">
        {isLoading ? (
          <SectionLoading label="Loading recognition" />
        ) : isError ? (
          <SectionError onRetry={refetch} />
        ) : (
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            {items.length === 0 ? (
              <SectionEmpty
                title="No awards published yet"
                description="Only verified company-level awards are listed here."
              />
            ) : (
              <StaggerChildren className="divide-y divide-[#1a2940] border-y border-[#1a2940]">
                {items.map((a) => (
                  <StaggerItem key={a.id}>
                    <div className="grid grid-cols-12 gap-6 py-10 items-center group">
                      <div className="col-span-3 lg:col-span-2 font-serif text-3xl md:text-5xl text-[#cca24d] tnum">
                        {a.year || "—"}
                      </div>
                      <div className="col-span-9 lg:col-span-8">
                        <div className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight group-hover:text-[#cca24d] transition-colors">
                          {a.title}
                        </div>
                        {a.organisation && (
                          <div className="mt-1 text-sm text-[#cca24d]/70">{a.organisation}</div>
                        )}
                        {a.description && (
                          <p className="mt-2 text-sm text-[#9ca3af] max-w-2xl leading-relaxed">
                            {a.description}
                          </p>
                        )}
                        {a.sourceUrl && (
                          <a
                            href={a.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#9ca3af] hover:text-[#cca24d] transition-colors"
                          >
                            View citation <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                      <div className="col-span-12 lg:col-span-2 flex lg:justify-end">
                        {a.image ? (
                          <img
                            src={a.image}
                            alt={a.title}
                            className="h-16 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                            loading="lazy"
                          />
                        ) : (
                          <Award
                            className="h-6 w-6 text-[#cca24d]/40 group-hover:text-[#cca24d] transition-colors"
                            strokeWidth={1}
                          />
                        )}
                      </div>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerChildren>
            )}
          </div>
        )}
      </section>
    </>
  );
}
